/* eslint-disable react/jsx-no-bind */
// Modal that lists the WhatsApp Business message templates the CRM has
// cached for a single connected page (phone_number_id). On open it
// fires syncWhatsAppTemplates so the user always sees a fresh view —
// the backend pulls from Meta's Graph API, upserts into
// whatsapp_templates, and returns the rows directly so we render
// without a second round trip.
//
// Read-only for now: this view is *display + refresh* only. Creating
// or editing templates still happens in Meta Business Manager (Meta
// owns the approval flow). A future "Send template" composer will
// pick from this same cache.

/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import moment from "moment";
import SYNC_WHATSAPP_TEMPLATES from "src/graphql/mutation/settings/syncWhatsAppTemplates";

interface WhatsAppTemplate {
  id: string;
  pageId: string;
  wabaId?: string | null;
  metaTemplateId?: string | null;
  name: string;
  language: string;
  category?: string | null;
  status?: string | null;
  qualityScore?: string | null;
  bodyText?: string | null;
  headerType?: string | null;
  headerText?: string | null;
  footerText?: string | null;
  buttons?: string | null;
  placeholderCount?: number | null;
  lastSyncedAt?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  pageId: string;
  pageName?: string;
  waNumber?: string;
}

// Status color map mirrors Meta's review states. Anything not in this
// map gets a neutral grey chip.
const STATUS_COLORS: Record<string, string> = {
  APPROVED: "#16a34a",
  PENDING: "#ca8a04",
  REJECTED: "#dc2626",
  PAUSED: "#dc2626",
  FLAGGED: "#ca8a04",
  DISABLED: "#6b7280",
  PENDING_DELETION: "#6b7280",
};

const CATEGORY_COLORS: Record<string, string> = {
  MARKETING: "#7c3aed",
  UTILITY: "#0891b2",
  AUTHENTICATION: "#0f766e",
};

const QUALITY_COLORS: Record<string, string> = {
  GREEN: "#16a34a",
  YELLOW: "#ca8a04",
  RED: "#dc2626",
  UNKNOWN: "#6b7280",
};

const WhatsAppTemplatesModal: React.FC<Props> = ({
  open,
  onClose,
  pageId,
  pageName,
  waNumber,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [search, setSearch] = useState("");
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  const [syncTemplates, { loading: syncing }] = useMutation(
    SYNC_WHATSAPP_TEMPLATES,
  );

  // Fire a sync on every open. Cheap UX win — user clicked "View
  // Templates" because they want the current state, not a stale one.
  useEffect(() => {
    if (!open || !pageId) return;
    (async () => {
      try {
        const res = await syncTemplates({ variables: { pageId } });
        const payload = res.data?.syncWhatsAppTemplates;
        if (payload?.success) {
          setTemplates(payload.templates || []);
          setLastSyncedAt(new Date().toISOString());
        } else {
          enqueueSnackbar(
            payload?.error || "Failed to fetch templates from Meta",
            { variant: "error" },
          );
          setTemplates(payload?.templates || []);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Failed to fetch templates", {
          variant: "error",
        });
      }
    })();
  }, [open, pageId]);

  const handleRefresh = async () => {
    try {
      const res = await syncTemplates({ variables: { pageId } });
      const payload = res.data?.syncWhatsAppTemplates;
      if (payload?.success) {
        setTemplates(payload.templates || []);
        setLastSyncedAt(new Date().toISOString());
        enqueueSnackbar(
          `Refreshed — ${payload.syncedCount ?? 0} templates synced`,
          { variant: "success" },
        );
      } else {
        enqueueSnackbar(payload?.error || "Refresh failed", {
          variant: "error",
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Refresh failed", { variant: "error" });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.bodyText?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.language?.toLowerCase().includes(q),
    );
  }, [templates, search]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
              WhatsApp Templates
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {pageName || "WhatsApp"} {waNumber ? `· ${waNumber}` : ""}
              {lastSyncedAt && (
                <>
                  {" · "}Last refreshed {moment(lastSyncedAt).fromNow()}
                </>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Refresh from Meta">
              <span>
                <IconButton onClick={handleRefresh} disabled={syncing}>
                  {syncing ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, body, category, language..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "text.secondary" }}
                />
              ),
            }}
          />
        </Box>

        {syncing && templates.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography sx={{ fontSize: 14 }}>
              {search.trim()
                ? "No templates match your search."
                : "No templates found yet. Create one in Meta Business Manager and refresh."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filtered.map((tpl) => {
              const statusColor = tpl.status
                ? STATUS_COLORS[tpl.status] || "#6b7280"
                : "#6b7280";
              const categoryColor = tpl.category
                ? CATEGORY_COLORS[tpl.category] || "#6b7280"
                : "#6b7280";
              const qualityColor = tpl.qualityScore
                ? QUALITY_COLORS[tpl.qualityScore] || "#6b7280"
                : null;

              let parsedButtons: Array<{ type: string; text: string }> = [];
              try {
                parsedButtons = tpl.buttons ? JSON.parse(tpl.buttons) : [];
              } catch {
                parsedButtons = [];
              }

              return (
                <Box
                  key={tpl.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                    background: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {tpl.name}
                    </Typography>
                    <Chip
                      label={tpl.language}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        bgcolor: "#e2e8f0",
                        color: "#334155",
                      }}
                    />
                    {tpl.category && (
                      <Chip
                        label={tpl.category}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          color: "white",
                          background: categoryColor,
                        }}
                      />
                    )}
                    {tpl.status && (
                      <Chip
                        label={tpl.status}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          color: "white",
                          background: statusColor,
                        }}
                      />
                    )}
                    {qualityColor && tpl.qualityScore !== "UNKNOWN" && (
                      <Tooltip title="Meta quality rating">
                        <Chip
                          label={`QS: ${tpl.qualityScore}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            color: "white",
                            background: qualityColor,
                          }}
                        />
                      </Tooltip>
                    )}
                    {(tpl.placeholderCount || 0) > 0 && (
                      <Chip
                        label={`${tpl.placeholderCount} ${
                          tpl.placeholderCount === 1 ? "variable" : "variables"
                        }`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          bgcolor: "#fde68a",
                          color: "#854d0e",
                        }}
                      />
                    )}
                  </Box>

                  {tpl.headerText && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#475569",
                        mb: 0.5,
                      }}
                    >
                      {tpl.headerText}
                    </Typography>
                  )}
                  {tpl.headerType && tpl.headerType !== "TEXT" && (
                    <Typography
                      sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}
                    >
                      Header: {tpl.headerType}
                    </Typography>
                  )}

                  {tpl.bodyText && (
                    <Typography
                      sx={{
                        fontSize: 13,
                        whiteSpace: "pre-wrap",
                        color: "#1e293b",
                      }}
                    >
                      {tpl.bodyText}
                    </Typography>
                  )}

                  {tpl.footerText && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.secondary",
                        mt: 0.5,
                        fontStyle: "italic",
                      }}
                    >
                      {tpl.footerText}
                    </Typography>
                  )}

                  {parsedButtons.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                        mt: 1,
                      }}
                    >
                      {parsedButtons.map((btn, idx) => (
                        <Chip
                          // Buttons have no stable id in Meta's payload,
                          // index is the natural key within a template.
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${tpl.id}-btn-${idx}`}
                          label={btn.text || btn.type}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppTemplatesModal;
